import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '../services/auth';

type AuthMode = 'STANDARD' | 'IMPERSONATION';
type SupportMode = 'READ_ONLY' | 'FULL_SUPPORT';

export interface AuthorizationState {
  authMode: AuthMode | null;
  supportMode: SupportMode | null;
  tenantID: string | null;
  supportSessionId: number | null;
  expiresAt: number | null;
}

const initialState: AuthorizationState = {
  authMode: null,
  supportMode: null,
  tenantID: null,
  supportSessionId: null,
  expiresAt: null,
};

/** Decode JWT payload without verifying signature (frontend display only). */
function decodeJwtClaims(token: string): Record<string, any> {
  try {
    const base64 = token.split('.')[1];
    return JSON.parse(atob(base64.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

function applyToken(state: AuthorizationState, token: string | undefined): void {
  const claims = token ? decodeJwtClaims(token) : {};
  const isValidImpersonation =
    claims.authMode === 'IMPERSONATION' &&
    typeof claims.exp === 'number' &&
    claims.exp * 1000 > Date.now();

  if (!isValidImpersonation) {
    Object.assign(state, initialState);
    return;
  }

  state.authMode = 'IMPERSONATION';
  state.supportMode =
    claims.supportMode === 'READ_ONLY' || claims.supportMode === 'FULL_SUPPORT'
      ? claims.supportMode
      : null;
  state.tenantID = String(claims.tenantId ?? claims.companyId ?? '');
  state.supportSessionId = Number(claims.supportSessionId) || null;
  state.expiresAt = claims.exp * 1000;
}

const authorizationSlice = createSlice({
  name: 'authorization',
  initialState,
  reducers: {
    setImpersonation: (
      state,
      action: PayloadAction<{
        authMode: AuthMode;
        supportMode: SupportMode;
        tenantID: string;
        supportSessionId?: number;
        expiresAt?: number;
      }>,
    ) => {
      state.authMode = action.payload.authMode;
      state.supportMode = action.payload.supportMode;
      state.tenantID = action.payload.tenantID;
      state.supportSessionId = action.payload.supportSessionId ?? null;
      state.expiresAt = action.payload.expiresAt ?? null;
    },
    restoreAuthorizationFromToken: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      applyToken(state, action.payload);
    },
    clearImpersonation: (state) => {
      state.authMode = null;
      state.supportMode = null;
      state.tenantID = null;
      state.supportSessionId = null;
      state.expiresAt = null;
    },
  },
  extraReducers: (builder) => {
    // Clear impersonation state on any standard login so stale support state never leaks
    builder
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state) => {
        state.authMode = null;
        state.supportMode = null;
        state.tenantID = null;
        state.supportSessionId = null;
        state.expiresAt = null;
      })
      .addMatcher(authApi.endpoints.loginAsClient.matchFulfilled, (state) => {
        state.authMode = null;
        state.supportMode = null;
        state.tenantID = null;
        state.supportSessionId = null;
        state.expiresAt = null;
      })
      // On support exchange: decode JWT claims → populate impersonation state
      .addMatcher(
        authApi.endpoints.exchangeSupportCode.matchFulfilled,
        (state, { payload }) => {
          if (!payload?.accessToken) return;

          applyToken(state, payload.accessToken);
        },
      )
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        Object.assign(state, initialState);
      })
      .addMatcher(authApi.endpoints.endSupportSession.matchFulfilled, (state) => {
        Object.assign(state, initialState);
      });
  },
});

export const {
  setImpersonation,
  restoreAuthorizationFromToken,
  clearImpersonation,
} = authorizationSlice.actions;
export default authorizationSlice.reducer;
